import Lottie from "lottie-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPlay } from "react-icons/fa";
import { useLocation, useParams } from "react-router-dom";

import correctAnimation from "../../../assets/json/correct_animation.json"; // Adjust path accordingly
import PointsIconBlue from "../../../assets/points_icon_blue.svg";
import ImageViewer from "../../../components/ImageViewer";
import LoadingSpinner from "../../../components/LoadingSpinner";
import CheckAnswerModal from "../../../components/modals/common/CheckAnswerModal";
import WatchVideoModal from "../../../components/modals/common/WatchVideoModal";
import VoiceSpeech from "../../../components/VoiceSpeech";
import { IDE_TYPE, USER_TYPE } from "../../../constants/common";
import useAuth from "../../../hooks/useAuth";
import lessonServices from "../../../services/lessonServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";
import StudentQuestionsResults from "./StudentQuestionsResults";

const StudentLessonQuestions = () => {
  const { id: lessonID } = useParams();
  const location = useLocation();
  const lang = getLanguage();
  const user = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigateWithLang();

  const lessonNameQuery = location.state?.lessonName;
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answerChecked, setAnswerChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [correctAnswersBySkill, setCorrectAnswersBySkill] = useState({});
  const [correctOptionId, setCorrectOptionId] = useState(null);
  const [isFinished, setIsFinished] = useState(false);
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const watchVideoModalRef = useRef();
  const checkAnswerModalRef = useRef();
  const [lessonName, setLessonName] = useState(lessonNameQuery || "");

  const openWatchVideoModal = (lessonID, videoType) =>
    watchVideoModalRef.current?.openModal(lessonID, videoType);

  const openCheckAnswerModal = () => checkAnswerModalRef.current?.openModal();

  const getRemainingEligibleCount = (currentIdx, skillsMap) => {
    let count = 0;
    for (let i = currentIdx; i < questions.length; i++) {
      const skillId = questions[i].skill_id;
      if (!skillsMap[skillId] || skillsMap[skillId] < 2) {
        count++;
      }
    }
    return count;
  };

  const fetchLessonQuestions = async (lessonID) => {
    try {
      const response = await lessonServices.getStudentLessonQuestions(lessonID);
      setQuestions(response?.questions);
      setIsFinished(response?.is_finished || response?.questions.length === 0);
      setLessonName(response?.lesson?.title);
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLessonQuestions(lessonID);
  }, [lessonID, lang]);

  useEffect(() => {
    const handleQuestionFlow = async () => {
      if (isLoading) return; // wait for loading to complete

      if (questions?.length > 0 && currentIndex < questions?.length) {
        const question = questions[currentIndex];
        setCurrentQuestion(question);

        if (question.solution) {
          setSelectedOption(question.solution.option_id);
          setIsCorrect(question.solution.is_correct);

          // If correct, selected option is correct
          if (question.solution.is_correct) {
            setCorrectOptionId(question.solution.option_id);
          } else {
            // If incorrect, find the correct one
            const correct = question.options.find((opt) => opt.is_correct);
            setCorrectOptionId(correct?.id ?? null);
          }

          setAnswerChecked(true);
        }
      } else if (!isLoading && questions?.length === 0) {
        console.warn("No questions available");
      } else {
        try {
          setIsSubmitting(true);
          await lessonServices.finishLesson(lessonID);
          await lessonServices.takeQuestionsPoints(lessonID);
          setIsSubmitting(false);
          navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area`);
        } catch (error) {
          console.error("Failed to finish lesson:", error);
        }
      }
    };

    handleQuestionFlow();
  }, [currentIndex, questions, isLoading]);

  const handleOptionChange = (optionId) => {
    if (!answerChecked) setSelectedOption(optionId);
  };

  const playSound = (isCorrect) => {
    const correctSound = document.getElementById("audio");
    const incorrectSound = document.getElementById("audio1");

    if (isCorrect) {
      correctSound?.play();
    } else {
      incorrectSound?.play();
    }
  };

  const handleCheckOrNext = async () => {
    if (answerChecked) {
      const skillId = currentQuestion?.skill_id;
      let updatedCorrectAnswersBySkill = { ...correctAnswersBySkill };

      if (isCorrect) {
        updatedCorrectAnswersBySkill[skillId] = (updatedCorrectAnswersBySkill[skillId] || 0) + 1;
        setCorrectAnswersBySkill(updatedCorrectAnswersBySkill);
      }

      const nextIndex = getNextIndex(currentIndex, updatedCorrectAnswersBySkill);

      // Start fake loading
      setIsTransitioning(true);

      // Wait for a short time before showing next question
      setTimeout(() => {
        setSelectedOption(null);
        setAnswerChecked(false);
        setIsCorrect(null);
        setCurrentIndex(nextIndex);
        setIsTransitioning(false); // End fake loading
      }, 200); // 200ms
      return;
    }

    // Normal answer checking flow
    if (selectedOption === null || !currentQuestion) {
      openCheckAnswerModal();
      return;
    }
    try {
      setLoadingAnswer(true);
      const res = await lessonServices.checkAnswer(currentQuestion?.id, selectedOption);
      setIsCorrect(res?.is_correct);
      setCorrectOptionId(res?.correct_answer_id ?? selectedOption);
      setAnswerChecked(true);
      playSound(res?.is_correct);
    } catch (err) {
      console.error("Error checking answer", err);
      setIsCorrect(false);
      setCorrectOptionId(null);
      setAnswerChecked(true);
      playSound(false);
    } finally {
      setLoadingAnswer(false);
    }
  };

  const getNextIndex = (startIndex, skillsMap) => {
    for (let i = startIndex + 1; i < questions.length; i++) {
      const skillId = questions[i].skill_id;
      if (!skillsMap[skillId] || skillsMap[skillId] < 2) {
        return i;
      }
    }
    return questions.length; // Finish lesson
  };

  const getSkillMapWithCurrent = () => {
    let updatedMap = { ...correctAnswersBySkill };
    if (answerChecked && isCorrect && currentQuestion?.skill_id) {
      const skillId = currentQuestion.skill_id;
      updatedMap[skillId] = (updatedMap[skillId] || 0) + 1;
    }
    return updatedMap;
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isFinished) return <StudentQuestionsResults lessonName={lessonName} />;

  const skillMapWithCurrent = getSkillMapWithCurrent();
  const remainingCount = getRemainingEligibleCount(currentIndex, skillMapWithCurrent);
  const isLastQuestion = remainingCount === 1 || remainingCount === 0;

  return (
    <div className="relative flex flex-col items-center text-blue-500">
      {/* Header */}
      <div className="mb-4 flex w-full items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <span role="img" aria-label="bulb">
            💡
          </span>
          {lessonName}
        </h1>
        {/* <div className="flex items-center gap-2">
          <span className="mr-1 bg-white text-blue-500">
            <img src={PointsIconBlue} alt="" />
          </span>
          {t("you_can_earn_points")} <span className="font-bold text-green-500">100 pts static</span>
        </div> */}
      </div>

      <div className="relative w-full">
        {/* Overlay when transitioning */}
        {(isTransitioning || isSubmitting) && (
          <div className="absolute inset-0 z-50 flex items-center justify-center rounded-3xl bg-white bg-opacity-80">
            <span className="h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></span>
          </div>
        )}

        {/* Question Box */}
        <div className="w-full rounded-3xl border-2 border-blue-500 bg-white p-6">
          <div className="mb-2 flex items-start justify-between">
            {user.IDEType == IDE_TYPE.WEB ? (
              <h2 className="text-lg font-bold text-blue-500 md:text-2xl">{currentQuestion?.text}</h2>
            ) : (
              <h2
                className="text-lg font-bold text-blue-500 md:text-2xl"
                dangerouslySetInnerHTML={{ __html: currentQuestion?.text }}
              ></h2>
            )}

            <VoiceSpeech questionText={currentQuestion?.text} voice_field={currentQuestion?.voice_field} />
          </div>
          {currentQuestion?.image && <ImageViewer src={currentQuestion.image} className="mb-4" />}
          {/* Options */}
          <div className={currentQuestion?.type?.id === 2 ? "grid grid-cols-2 gap-2" : "flex flex-col gap-2"}>
            {currentQuestion?.options?.map((option) => {
              const isSelected = selectedOption === option.id;
              const isCorrectAnswer = answerChecked && option.id === correctOptionId;
              const isIncorrectSelected = answerChecked && isSelected && option.id !== correctOptionId;

              const baseClasses =
                "relative flex w-full sm:w-fit cursor-pointer items-center gap-4 rounded-xl p-3";
              const selectedClasses =
                isSelected && !answerChecked ? "border-blue-500 ring-2 ring-blue-200" : "";
              const correctClasses = isCorrectAnswer ? "option-wrapper-green draw-border" : "";
              const incorrectClasses = isIncorrectSelected ? "option-wrapper-red draw-border" : "";

              return (
                <label
                  key={option.id}
                  className={`${baseClasses} ${selectedClasses} ${correctClasses} ${incorrectClasses} bg-white`}
                >
                  {isCorrectAnswer && (
                    <div className="pointer-events-none absolute top-1/2 z-10 w-16 -translate-y-1/2 ltr:right-[-30px] rtl:left-[-30px]">
                      <Lottie animationData={correctAnimation} loop={false} />
                    </div>
                  )}
                  <input
                    type="radio"
                    name="answer"
                    value={option.id}
                    checked={isSelected}
                    onChange={() => handleOptionChange(option.id)}
                    disabled={answerChecked}
                    className="h-5 w-5 text-gray-900 accent-blue-500"
                  />

                  {currentQuestion?.type?.id === 1 ? (
                    user.IDEType == IDE_TYPE.WEB ? (
                      <div className="py-4 text-xl font-medium text-gray-800 ltr:pr-16 rtl:pl-16">
                        {option.text}
                      </div>
                    ) : (
                      <div
                        className="py-4 text-xl font-medium text-gray-800 ltr:pr-16 rtl:pl-16"
                        dangerouslySetInnerHTML={{ __html: option.text }}
                      ></div>
                    )
                  ) : (
                    <img
                      src={option.image}
                      alt={`Option ${option.id}`}
                      loading="lazy"
                      className="h-36 w-36 rounded-xl object-contain pr-6"
                    />
                  )}
                </label>
              );
            })}
          </div>
          {/* <div className="mt-6 flex justify-end gap-2">
            <img src={PointsIconBlue} alt="" />
            <span className="font-bold text-green-500">30 pts</span>
          </div> */}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="mt-4 flex w-full flex-col justify-between md:flex-row md:items-center">
        <button
          className="flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 font-semibold text-white transition hover:bg-yellow-500"
          onClick={() => openWatchVideoModal(lessonID, "lesson")}
        >
          <FaPlay className="text-sm" /> {t("watch_lesson_again")}
        </button>
        {!isTransitioning && !isSubmitting && (
          <button
            onClick={handleCheckOrNext}
            disabled={loadingAnswer}
            className={`wavy-btn relative mt-1 flex w-full flex-col items-center justify-center gap-1 rounded-3xl border-4 border-[#96B8F3] p-4 text-lg font-semibold text-white transition duration-200 md:w-2/6 ${
              loadingAnswer ? "cursor-not-allowed bg-gray-400" : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            {loadingAnswer ? (
              <span className="h-7 w-7 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
            ) : answerChecked ? (
              <>
                {currentQuestion?.solution && (
                  <span className="px-3 text-xs font-medium text-yellow-200">
                    {t("you_already_answered_this_question")}
                  </span>
                )}
                <span>{isLastQuestion ? t("submit") : t("go_to_next_question")}</span>
              </>
            ) : (
              t("check_my_answer")
            )}
          </button>
        )}
      </div>
      <audio id="audio">
        <source src="https://cdn-static-images-frontend.s3-eu-west-1.amazonaws.com/sound/questionsSound/correct1.mp3" />
      </audio>
      <audio id="audio1">
        <source src="https://cdn-static-images-frontend.s3-eu-west-1.amazonaws.com/sound/questionsSound/incoreect.mp3" />
      </audio>
      <WatchVideoModal ref={watchVideoModalRef} />
      <CheckAnswerModal ref={checkAnswerModalRef} />
    </div>
  );
};

export default StudentLessonQuestions;
